import React, { useState, useMemo } from 'react';
import { Check, Info, Mail, MessageSquare, MapPin } from '../lib/icons';
import ImageSlot from './ImageSlot';

interface OrderContact {
  name: string;
  contactInfo: string;
  pickup: 'san-diego' | 'temecula' | 'mail' | '';
  address: string;
}

interface BuildOptions {
  shellFinish: string;
  rgbTrim: boolean;
  tmrJoysticks: boolean;
  clickyTriggers: boolean;
  mouseClickButtons: boolean;
  rumbleDelete: boolean;
  backChoice: 'none' | 'standard' | 'pro' | 'buttons';
  backButtonPressure: '100g' | '160g' | '200g';
  backButtonSize: 'small' | 'large';
}

const PRICING = {
  base: 190,
  designerFinish: 50,
  rgbTrim: 35,
  clickyTriggers: 60,
  mouseClickButtons: 50,
  rumbleDelete: 25,
  backButtons: 80,
  backPaddles: { none: 0, standard: 80, pro: 120 },
};

const shellFinishes = [
  { id: 'glossy', name: 'Glossy', description: 'High-shine premium finish' },
  { id: 'matte', name: 'Matte', description: 'Soft-touch textured grip' },
  { id: 'clear', name: 'Clear', description: 'Transparent shell' },
  { id: 'designer', name: 'Designer', description: 'Custom designer patterns' },
];

const PS5Builder = () => {
  const [options, setOptions] = useState<BuildOptions>({
    shellFinish: 'glossy',
    rgbTrim: false,
    tmrJoysticks: true,
    clickyTriggers: false,
    mouseClickButtons: false,
    rumbleDelete: false,
    backChoice: 'none',
    backButtonPressure: '160g',
    backButtonSize: 'small',
  });

  const [showTip, setShowTip] = useState<string | null>(null);
  const [orderContact, setOrderContact] = useState<OrderContact>({
    name: '',
    contactInfo: '',
    pickup: '',
    address: '',
  });

  const total = useMemo(() => {
    let p = PRICING.base;
    if (options.shellFinish === 'designer') p += PRICING.designerFinish;
    if (options.rgbTrim) p += PRICING.rgbTrim;
    if (options.clickyTriggers) p += PRICING.clickyTriggers;
    if (options.mouseClickButtons) p += PRICING.mouseClickButtons;
    if (options.rumbleDelete) p += PRICING.rumbleDelete;
    if (options.backChoice === 'standard') p += PRICING.backPaddles.standard;
    if (options.backChoice === 'pro') p += PRICING.backPaddles.pro;
    if (options.backChoice === 'buttons') p += PRICING.backButtons;
    return p;
  }, [options]);

  const set = (key: keyof BuildOptions, value: BuildOptions[keyof BuildOptions]) =>
    setOptions(prev => ({ ...prev, [key]: value }));

  const toggle = (key: keyof BuildOptions) =>
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));

  const isContactValid = () => {
    if (!orderContact.name || !orderContact.contactInfo || !orderContact.pickup) return false;
    if (orderContact.pickup === 'mail' && !orderContact.address) return false;
    return true;
  };

  const buildSummaryText = () => {
    const lines = [
      'CUSTOM PS5 CONTROLLER BUILD',
      '═══════════════════════════',
      `Shell Finish: ${shellFinishes.find(f => f.id === options.shellFinish)?.name}`,
      `RGB Trim Rings: ${options.rgbTrim ? 'Yes' : 'No'}`,
      '',
      'PERFORMANCE UPGRADES:',
      '  ✓ TMR Hall-Effect Joysticks (Anti-Drift) — Included',
      options.clickyTriggers ? `  ✓ Clicky Triggers/Bumpers (+$${PRICING.clickyTriggers})` : null,
      options.mouseClickButtons ? `  ✓ Clicky Face Buttons & D-Pad (+$${PRICING.mouseClickButtons})` : null,
      options.rumbleDelete ? `  ✓ Rumble Motor Delete (+$${PRICING.rumbleDelete})` : null,
      options.backChoice === 'standard' ? `  ✓ Standard Back Paddles (+$${PRICING.backPaddles.standard})` : null,
      options.backChoice === 'pro' ? `  ✓ Pro Back Paddles (+$${PRICING.backPaddles.pro})` : null,
      options.backChoice === 'buttons' ? `  ✓ Back Buttons up to 4 — ${options.backButtonSize === 'small' ? 'Small' : 'Large'}, ${options.backButtonPressure} (+$${PRICING.backButtons})` : null,
      '',
      `TOTAL: $${total}`,
      '',
      'Note: Shell color, button colors, and trim color will be confirmed during order.',
    ].filter(v => v !== null).join('\n');

    const contact = `\n\nCUSTOMER INFO:\n  Name: ${orderContact.name}\n  Contact: ${orderContact.contactInfo}\n  Pickup: ${
      orderContact.pickup === 'san-diego' ? 'San Diego'
      : orderContact.pickup === 'temecula' ? 'Temecula'
      : `Mail to: ${orderContact.address}`
    }`;

    return lines + contact + '\n\nPlease send me an invoice for this build.';
  };

  const handleEmail = () => {
    window.location.href = `mailto:info@gamefixsolutions.com?subject=${encodeURIComponent('Custom PS5 Controller Order')}&body=${encodeURIComponent(buildSummaryText())}`;
  };

  const handleSMS = () => {
    window.open(`sms:+19512168534?body=${encodeURIComponent(buildSummaryText())}`, '_blank');
  };

  const Tip = ({ id, text }: { id: string; text: string }) => (
    <span
      onMouseEnter={() => setShowTip(id)}
      onMouseLeave={() => setShowTip(null)}
      onClick={e => e.stopPropagation()}
      className="relative shrink-0 cursor-help"
    >
      <Info className="h-4 w-4 text-[#00BFFF]" />
      {showTip === id && (
        <div className="absolute left-0 top-6 w-60 bg-black border border-gray-700 rounded-lg p-3 text-xs text-[#B0B0B0] z-20 shadow-xl">
          {text}
        </div>
      )}
    </span>
  );

  return (
    <div className="bg-gradient-to-b from-[#0D0D0D] to-black py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">Custom PS5 Builder</h2>
          <p className="text-xl text-[#B0B0B0]">Select your upgrades — price updates in real time</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ── Options ── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Shell Finish */}
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
              <h3 className="text-xl font-bold text-[#E0E0E0] mb-4">Shell Finish</h3>
              <div className="grid grid-cols-2 gap-3">
                {shellFinishes.map(finish => (
                  <button
                    key={finish.id}
                    onClick={() => set('shellFinish', finish.id)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      options.shellFinish === finish.id
                        ? 'border-[#39FF14] bg-[#39FF14]/10'
                        : 'border-gray-700 hover:border-gray-600 bg-[#0D0D0D]'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-[#E0E0E0] font-semibold">{finish.name}</p>
                        <p className="text-xs text-[#B0B0B0] mt-0.5">{finish.description}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        {finish.id === 'designer' && (
                          <span className="text-[#39FF14] text-sm font-semibold">+$50</span>
                        )}
                        {options.shellFinish === finish.id && <Check className="h-4 w-4 text-[#39FF14]" />}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* RGB Trim */}
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
              <h3 className="text-xl font-bold text-[#E0E0E0] mb-4">Trim Rings</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: false, label: 'Standard', sub: 'Colored accent rings', slotId: 'builder-trim-standard' },
                  { id: true,  label: 'RGB LED',  sub: 'Programmable light rings', slotId: 'builder-trim-rgb-led' },
                ].map(opt => (
                  <div key={String(opt.id)} className="flex flex-col gap-2">
                    {/* Photo preview */}
                    <div className="aspect-video rounded-xl overflow-hidden bg-gray-950">
                      <ImageSlot
                        slotId={opt.slotId}
                        fallbackSrc=""
                        fallbackAlt={opt.label}
                        className="object-cover"
                        wrapperClassName="w-full h-full"
                      />
                    </div>
                    <button
                      onClick={() => set('rgbTrim', opt.id)}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        options.rgbTrim === opt.id
                          ? 'border-[#39FF14] bg-[#39FF14]/10'
                          : 'border-gray-700 hover:border-gray-600 bg-[#0D0D0D]'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-[#E0E0E0] font-semibold">{opt.label}</p>
                          <p className="text-xs text-[#B0B0B0] mt-0.5">{opt.sub}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          {opt.id && <span className="text-[#39FF14] text-sm font-semibold">+$35</span>}
                          {options.rgbTrim === opt.id && <Check className="h-4 w-4 text-[#39FF14]" />}
                        </div>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance Upgrades */}
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
              <h3 className="text-xl font-bold text-[#E0E0E0] mb-4">Performance Upgrades</h3>
              <div className="space-y-3">

                {/* TMR — always included */}
                <div className="p-4 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <p className="text-[#E0E0E0] font-semibold">TMR Hall-Effect Joysticks</p>
                    <Tip id="tmr" text="Magnetic sensors replace physical potentiometers — eliminates stick drift permanently." />
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[#39FF14] font-semibold text-sm">Included</span>
                    <div className="w-5 h-5 rounded bg-[#39FF14] flex items-center justify-center">
                      <Check className="h-3 w-3 text-black" />
                    </div>
                  </div>
                </div>

                {/* Toggle upgrades */}
                {[
                  { key: 'clickyTriggers' as const, label: 'Clicky Triggers & Bumpers', sub: 'Hair-trigger travel, instant actuation', price: PRICING.clickyTriggers, tip: 'tmr-triggers', tipText: 'Reduces trigger travel by ~70%. Satisfying click, faster response for FPS.' },
                  { key: 'mouseClickButtons' as const, label: 'Clicky Face Buttons & D-Pad', sub: 'All face buttons + D-Pad', price: PRICING.mouseClickButtons, tip: 'tmr-buttons', tipText: 'Mechanical switch on every face button. Crisp tactile click on X, O, □, △, and D-Pad.' },
                  { key: 'rumbleDelete' as const, label: 'Rumble Motor Delete', sub: 'Lighter build, zero vibration', price: PRICING.rumbleDelete, tip: 'rumble', tipText: 'Both vibration motors removed. Popular with competitive players who want the lightest possible controller.' },
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={() => toggle(item.key)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                      options[item.key] ? 'border-[#39FF14] bg-[#39FF14]/10' : 'border-gray-700 hover:border-gray-600 bg-[#0D0D0D]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-[#E0E0E0] font-semibold">{item.label}</p>
                          <Tip id={item.tip} text={item.tipText} />
                        </div>
                        <p className="text-xs text-[#B0B0B0] mt-0.5">{item.sub}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-4">
                        <span className="text-[#39FF14] font-semibold text-sm">+${item.price}</span>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${options[item.key] ? 'bg-[#39FF14] border-[#39FF14]' : 'border-gray-600'}`}>
                          {options[item.key] && <Check className="h-3 w-3 text-black" />}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}

                {/* Paddles or Buttons */}
                <div className="space-y-2 pt-1">
                  <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-wide px-1">Paddles or Buttons?</p>
                  {([
                    { id: 'none',     label: 'None',             sub: 'Standard layout',                     price: null },
                    { id: 'standard', label: 'Standard Paddles', sub: '4 programmable, remappable',          price: PRICING.backPaddles.standard },
                    { id: 'pro',      label: 'Pro Paddles',      sub: 'OLED · profiles · adj. triggers',    price: PRICING.backPaddles.pro, badge: 'PREMIUM' },
                    { id: 'buttons',  label: 'Back Buttons',     sub: 'Up to 4 — pairing fixed at build',   price: PRICING.backButtons, tip: true },
                  ] as const).map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => set('backChoice', opt.id)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        options.backChoice === opt.id ? 'border-[#39FF14] bg-[#39FF14]/10' : 'border-gray-700 hover:border-gray-600 bg-[#0D0D0D]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[#E0E0E0] font-semibold">{opt.label}</span>
                            {opt.badge && (
                              <span className="text-xs bg-gradient-to-r from-[#FF1744] to-[#39FF14] text-white px-1.5 py-0.5 rounded font-bold">{opt.badge}</span>
                            )}
                            {opt.tip && (
                              <Tip id="backbtns" text="Click pressure (100g / 160g / 200g) and size are chosen below. Button pairing is set at installation and stays fixed." />
                            )}
                          </div>
                          <p className="text-xs text-[#B0B0B0] mt-0.5">{opt.sub}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-4">
                          {opt.price !== null && (
                            <span className="text-[#39FF14] font-semibold text-sm">+${opt.price}</span>
                          )}
                          {options.backChoice === opt.id && <Check className="h-4 w-4 text-[#39FF14]" />}
                        </div>
                      </div>
                    </button>
                  ))}

                  {options.backChoice === 'buttons' && (
                    <div className="ml-2 p-4 bg-[#0D0D0D] rounded-xl border border-gray-700 space-y-4">
                      <div>
                        <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-wide mb-2">Click Pressure</p>
                        <div className="grid grid-cols-3 gap-2">
                          {(['100g', '160g', '200g'] as const).map(p => (
                            <button
                              key={p}
                              onClick={() => set('backButtonPressure', p)}
                              className={`py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                                options.backButtonPressure === p
                                  ? 'border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]'
                                  : 'border-gray-700 text-[#B0B0B0] hover:border-gray-500'
                              }`}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-wide mb-2">Button Size</p>
                        <div className="grid grid-cols-2 gap-2">
                          {([
                            { id: 'small', label: 'Small', sub: 'Lower profile' },
                            { id: 'large', label: 'Large', sub: 'Easier reach' },
                          ] as const).map(s => (
                            <button
                              key={s.id}
                              onClick={() => set('backButtonSize', s.id)}
                              className={`py-2 px-3 rounded-lg text-left border-2 transition-all ${
                                options.backButtonSize === s.id
                                  ? 'border-[#39FF14] bg-[#39FF14]/10'
                                  : 'border-gray-700 hover:border-gray-500'
                              }`}
                            >
                              <p className={`text-sm font-semibold ${options.backButtonSize === s.id ? 'text-[#39FF14]' : 'text-[#B0B0B0]'}`}>{s.label}</p>
                              <p className="text-xs text-[#B0B0B0]">{s.sub}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cosmetics note */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-xl px-5 py-4 text-sm text-[#B0B0B0]">
              Shell color, button kit, touchpad color, and trim color are confirmed with you during the build process.
            </div>
          </div>

          {/* ── Sticky Summary ── */}
          <div>
            <div className="sticky top-24 space-y-4">
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                <h3 className="text-lg font-bold text-[#E0E0E0] mb-4 flex items-center gap-2">
                  <Check className="h-5 w-5 text-[#39FF14]" /> Build Summary
                </h3>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between pb-3 border-b border-gray-800">
                    <span className="text-[#B0B0B0]">Base Controller</span>
                    <span className="text-[#E0E0E0] font-semibold">${PRICING.base}</span>
                  </div>
                  {options.shellFinish === 'designer' && (
                    <div className="flex justify-between">
                      <span className="text-[#B0B0B0]">Designer Finish</span>
                      <span className="text-[#39FF14]">+$50</span>
                    </div>
                  )}
                  {options.rgbTrim && (
                    <div className="flex justify-between">
                      <span className="text-[#B0B0B0]">RGB Trim Rings</span>
                      <span className="text-[#39FF14]">+$35</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#B0B0B0]">TMR Joysticks</span>
                    <span className="text-[#39FF14]">Included</span>
                  </div>
                  {options.clickyTriggers && (
                    <div className="flex justify-between">
                      <span className="text-[#B0B0B0]">Clicky Triggers</span>
                      <span className="text-[#39FF14]">+${PRICING.clickyTriggers}</span>
                    </div>
                  )}
                  {options.mouseClickButtons && (
                    <div className="flex justify-between">
                      <span className="text-[#B0B0B0]">Clicky Buttons</span>
                      <span className="text-[#39FF14]">+${PRICING.mouseClickButtons}</span>
                    </div>
                  )}
                  {options.rumbleDelete && (
                    <div className="flex justify-between">
                      <span className="text-[#B0B0B0]">Rumble Delete</span>
                      <span className="text-[#39FF14]">+${PRICING.rumbleDelete}</span>
                    </div>
                  )}
                  {options.backChoice === 'buttons' && (
                    <div className="flex justify-between">
                      <span className="text-[#B0B0B0]">Back Buttons ({options.backButtonSize === 'small' ? 'Sm' : 'Lg'} / {options.backButtonPressure})</span>
                      <span className="text-[#39FF14]">+${PRICING.backButtons}</span>
                    </div>
                  )}
                  {(options.backChoice === 'standard' || options.backChoice === 'pro') && (
                    <div className="flex justify-between">
                      <span className="text-[#B0B0B0]">{options.backChoice === 'pro' ? 'Pro' : 'Standard'} Paddles</span>
                      <span className="text-[#39FF14]">+${PRICING.backPaddles[options.backChoice]}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-gray-800">
                    <span className="text-[#E0E0E0] font-bold text-base">Total</span>
                    <span className="text-[#39FF14] font-bold text-2xl">${total}</span>
                  </div>
                </div>
              </div>

              {/* Contact + Order */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 space-y-3">
                <h4 className="text-sm font-semibold text-[#E0E0E0] uppercase tracking-wide flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#39FF14]" /> Your Info
                </h4>
                <div>
                  <label className="block text-xs text-[#B0B0B0] mb-1">Full Name *</label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={orderContact.name}
                    onChange={e => setOrderContact(p => ({ ...p, name: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm placeholder-gray-600 focus:outline-none focus:border-[#39FF14] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#B0B0B0] mb-1">Email or Phone *</label>
                  <input
                    type="text"
                    placeholder="email or phone"
                    value={orderContact.contactInfo}
                    onChange={e => setOrderContact(p => ({ ...p, contactInfo: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm placeholder-gray-600 focus:outline-none focus:border-[#39FF14] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#B0B0B0] mb-1">Pickup / Shipping *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['san-diego', 'temecula', 'mail'] as const).map(opt => (
                      <button
                        key={opt}
                        onClick={() => setOrderContact(p => ({ ...p, pickup: opt, address: opt !== 'mail' ? '' : p.address }))}
                        className={`py-2 rounded-lg text-xs font-semibold border-2 transition-all ${
                          orderContact.pickup === opt
                            ? 'border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]'
                            : 'border-gray-700 text-[#B0B0B0] hover:border-gray-500'
                        }`}
                      >
                        {opt === 'san-diego' ? 'San Diego' : opt === 'temecula' ? 'Temecula' : 'Mail'}
                      </button>
                    ))}
                  </div>
                </div>
                {orderContact.pickup === 'mail' && (
                  <div>
                    <label className="block text-xs text-[#B0B0B0] mb-1">Shipping Address *</label>
                    <textarea
                      placeholder="Street, City, State, ZIP"
                      value={orderContact.address}
                      onChange={e => setOrderContact(p => ({ ...p, address: e.target.value }))}
                      rows={2}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-[#E0E0E0] text-sm placeholder-gray-600 focus:outline-none focus:border-[#39FF14] transition-colors resize-none"
                    />
                  </div>
                )}
                <button
                  onClick={handleEmail}
                  disabled={!isContactValid()}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF1744] to-[#39FF14] hover:from-[#39FF14] hover:to-[#FF1744] text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Mail className="h-5 w-5" /> Order via Email
                </button>
                <button
                  onClick={handleSMS}
                  disabled={!isContactValid()}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <MessageSquare className="h-5 w-5" /> Order via SMS
                </button>
                {!isContactValid() && (
                  <p className="text-xs text-[#B0B0B0] text-center">Fill in your info above to place your order</p>
                )}
                <p className="text-xs text-[#B0B0B0] text-center pt-1">2–4 day turnaround · 90-day warranty</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PS5Builder;
